import React, { useRef } from "react";
import { Link } from "react-router-dom";
import {
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from "react-share";
import { useTheme } from "../../context/ThemeContext/ThemeProviderContext";
import { Theme } from "../../definitions/enum";
import PopoverDynamic from "../dialog/PopoverDynamic";
import copyToClipboard from "../../utilities/copyToClipboard";
import IcLinkSVG from "../IcLinkSVG";
import usePDFViewer from "../../hooks/usePDFViewer";

interface Breadcrumb {
  label: string;
  link?: string;
}

interface PDFViewerProps {
  pdfUrl: string;
  onBack: () => void;
  title: string;
  date?: string;
  breadcrumbs?: Breadcrumb[];
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfUrl,
  onBack,
  title,
  date,
  breadcrumbs = [{ label: "Documents", link: "/documents" }, { label: "PDF" }],
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const shareUrl = window.location.href;
  const downloadFileName =
    title || (pdfUrl ? pdfUrl.split("/").pop() : null) || "document.pdf";

  const {
    canvasRef,
    currentPage,
    totalPages,
    pageInput,
    loading,
    currentScale,
    handlePrevPage,
    handleNextPage,
    handlePageInputChange,
    handlePageJump,
    handlePageInputKeyDown,
    handleZoomIn,
    handleZoomOut,
    handleRotate,
  } = usePDFViewer({ pdfUrl });

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl);
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      }
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = downloadFileName;
      link.click();
    }
  };

  return (
    <>
      <div className="container mx-auto py-12 phone-s:max-w-full laptop:w-11/12">
        <div className="card bg-neutral shadow-xl">
          <div className="card-body">
            <div className="breadcrumbs text-sm" style={{ color: "#999" }}>
              <ul>
                {breadcrumbs.map((crumb, index) => (
                  <li key={index}>
                    {crumb.link ? (
                      <Link to={crumb.link}>{crumb.label}</Link>
                    ) : (
                      crumb.label
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <h2 className="card-title text-4xl">{title}</h2>
            {date && <div className="text-sm text-white">{date}</div>}
            <div className="mb-6 flex items-center space-x-2">
              <PopoverDynamic
                renderPopoverContent={() => (
                  <div className="tooltip-headless absolute -top-[calc(100%+68px)] left-1 mt-2 max-w-[300px] rounded-lg border-2 border-black bg-white px-5 py-3 text-sm font-medium text-black">
                    Link berhasil disalin
                  </div>
                )}
              >
                <IcLinkSVG
                  fill={theme === Theme.DARK ? "#FFF" : "#000"}
                  className="h-6 w-6 cursor-pointer"
                  onClick={() => copyToClipboard(shareUrl)}
                />
              </PopoverDynamic>

              <FacebookShareButton url={shareUrl}>
                <FacebookIcon size={24} />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl}>
                <XIcon size={24} />
              </TwitterShareButton>
              <WhatsappShareButton url={shareUrl}>
                <WhatsappIcon size={24} />
              </WhatsappShareButton>
            </div>

            <div className="flex h-full flex-col" ref={containerRef}>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between bg-base-300 p-4 text-base-content">
                <button onClick={onBack} className="btn btn-primary btn-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-1 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Back
                </button>

                <div className="flex items-center">
                  <button
                    onClick={handleZoomOut}
                    className="btn btn-ghost btn-sm mr-2"
                    aria-label="Zoom out"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  <span className="mx-2">{currentScale}%</span>

                  <button
                    onClick={handleZoomIn}
                    className="btn btn-ghost btn-sm ml-2"
                    aria-label="Zoom in"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={handleRotate}
                    className="btn btn-ghost btn-sm ml-4"
                    aria-label="Rotate"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={handlePrint}
                    className="btn btn-ghost btn-sm ml-4"
                    aria-label="Print"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={handleDownload}
                    className="btn btn-ghost btn-sm ml-4"
                    aria-label="Download"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center">
                  <button
                    onClick={handlePrevPage}
                    className="btn btn-ghost btn-sm"
                    disabled={currentPage <= 1}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  <div className="mx-2 flex items-center">
                    <input
                      type="text"
                      className="input input-bordered input-sm w-12 text-center"
                      value={pageInput}
                      onChange={handlePageInputChange}
                      onBlur={handlePageJump}
                      onKeyDown={handlePageInputKeyDown}
                      aria-label="Page number"
                    />
                    <span className="bg-base-200 px-2 py-1 text-base-content rounded-r border border-l-0 border-base-300">
                      of {totalPages}
                    </span>
                  </div>

                  <button
                    onClick={handleNextPage}
                    className="btn btn-ghost btn-sm"
                    disabled={currentPage >= totalPages}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* PDF Viewer */}
              <div className="flex flex-1 justify-center overflow-auto bg-base-200 p-4">
                {loading ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="loading loading-spinner loading-lg text-primary"></div>
                  </div>
                ) : (
                  <canvas ref={canvasRef} className="shadow-lg" />
                )}
              </div>

              <div className="bg-base-300 p-2 text-center text-sm text-base-content">
                Gunakan tombol panah ← dan → untuk pindah halaman
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PDFViewer;
